import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useAppsCatalogPreviewQuery } from '@/client/api/apps-catalog/apps-catalog.query';
import type { AppCatalogSite, AppCatalogSurface } from '@/client/types/apps-catalog';
import { AppsCatalogHeader } from '@/client/components/admin/AppsCatalog/AppsCatalogHeader';
import { CatalogTile, siteLabel } from '@/client/components/admin/AppsCatalog/catalogUi';

const SITES: AppCatalogSite[] = ['marketplace', 'homes'];
const SURFACES: { id: AppCatalogSurface; label: string }[] = [
  { id: 'all', label: 'All apps' },
  { id: 'grid', label: 'Header grid' },
  { id: 'store', label: 'Store' },
];

const PRESETS: { label: string; site: AppCatalogSite; surface: AppCatalogSurface }[] = [
  { label: 'Marketplace header', site: 'marketplace', surface: 'grid' },
  { label: 'Homes header', site: 'homes', surface: 'grid' },
  { label: 'Store', site: 'marketplace', surface: 'store' },
];

const Segmented = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) => (
  <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        onClick={() => onChange(option.id)}
        className={cn(
          'rounded px-3 py-1.5 text-sm font-medium transition-colors',
          value === option.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800',
        )}
        aria-pressed={value === option.id}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const AppsCatalogPreviewPage = () => {
  useBreadcrumbs([{ label: 'Apps catalog', href: '/admin/apps-catalog' }, { label: 'Preview' }]);

  const [site, setSite] = useState<AppCatalogSite>('marketplace');
  const [surface, setSurface] = useState<AppCatalogSurface>('grid');
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);

  const { data, isLoading, isError, error, isFetching, refetch } = useAppsCatalogPreviewQuery({
    site,
    surface,
    include_unpublished: includeUnpublished,
    include_hidden: includeHidden,
  });
  const sections = data?.sections ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <AppsCatalogHeader
          active="preview"
          description="The catalog as a site sees it, built by the same rules as the public API. Unpublished apps and hidden sections appear only when you ask for them."
          actions={
            <Button variant="outline" className="gap-1.5" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              Refresh
            </Button>
          }
        />

        <div className="space-y-4 rounded-lg border bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Quick view</span>
            {PRESETS.map((preset) => {
              const active = preset.site === site && preset.surface === surface;
              return (
                <Button
                  key={preset.label}
                  size="sm"
                  variant={active ? 'default' : 'outline'}
                  onClick={() => {
                    setSite(preset.site);
                    setSurface(preset.surface);
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t pt-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Site</span>
              <Segmented
                options={SITES.map((s) => ({ id: s, label: siteLabel(s) }))}
                value={site}
                onChange={setSite}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Surface</span>
              <Segmented options={SURFACES} value={surface} onChange={setSurface} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={includeUnpublished}
                onChange={(e) => setIncludeUnpublished(e.target.checked)}
              />
              Include draft and unpublished apps
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={includeHidden}
                onChange={(e) => setIncludeHidden(e.target.checked)}
              />
              Include hidden sections
            </label>
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        )}

        {!isLoading && isError && (
          <div className="rounded-lg border bg-white p-8 text-center text-sm text-red-600 shadow-sm">
            {getApiErrorMessage(error, 'Could not load the preview.')}
          </div>
        )}

        {!isLoading && !isError && sections.length === 0 && (
          <div className="rounded-lg border bg-white p-8 text-center text-sm text-gray-500 shadow-sm">
            {siteLabel(site)} would show no sections here.
          </div>
        )}

        {!isLoading && !isError && sections.length > 0 && (
          <div className={cn('space-y-4', isFetching && 'opacity-70')}>
            {sections.map((section) => {
              const hidden = section.visible === false;
              return (
                <section
                  key={section.key}
                  className={cn(
                    'rounded-lg border bg-white p-4 shadow-sm',
                    hidden && 'border-dashed bg-gray-50',
                  )}
                >
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                      {section.label}
                    </h2>
                    <span className="font-mono text-[11px] text-gray-400">{section.key}</span>
                    <span className="text-xs text-gray-500">
                      {section.apps.length} {section.apps.length === 1 ? 'app' : 'apps'}
                    </span>
                    {hidden && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                        <EyeOff className="h-3 w-3" />
                        Hidden section
                      </span>
                    )}
                  </div>
                  {section.apps.length === 0 ? (
                    <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-gray-500">
                      No apps. ff-ui renders this section as Coming soon.
                    </p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {section.apps.map((app) => (
                        <Link
                          key={app.app_key}
                          to={`/admin/apps-catalog/apps/${encodeURIComponent(app.app_key)}`}
                          title={`Edit ${app.name}`}
                        >
                          <CatalogTile app={app} showFlags={surface === 'all'} />
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppsCatalogPreviewPage;
